// frontend/src/modules/projects/components/EditEntryDialog.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { projectsApi } from '../api';
import type { Task, TimeEntry } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  entry: TimeEntry | null;
  tasks: Task[];
  onSaved: () => void;
}

const NO_TASK = '__none__';

function splitLocal(iso: string | null): { date: string; time: string } {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

export function EditEntryDialog({ open, onOpenChange, entry, tasks, onSaved }: Props) {
  const [taskId, setTaskId] = React.useState<string>(NO_TASK);
  const [startDate, setStartDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [billable, setBillable] = React.useState(true);
  const [hourlyRate, setHourlyRate] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open || !entry) return;
    setTaskId(entry.task_id ?? NO_TASK);
    const s = splitLocal(entry.start_at);
    setStartDate(s.date); setStartTime(s.time);
    const e = splitLocal(entry.end_at);
    setEndDate(e.date); setEndTime(e.time);
    setNotes(entry.notes ?? '');
    setBillable(entry.billable !== false);
    setHourlyRate(entry.hourly_rate != null ? String(entry.hourly_rate) : '');
  }, [open, entry]);

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!entry) return;
    const start = new Date(`${startDate}T${startTime}:00`).toISOString();
    const end   = new Date(`${endDate}T${endTime}:00`).toISOString();
    if (new Date(end).getTime() <= new Date(start).getTime()) {
      toast.error('End must be after start'); return;
    }
    setBusy(true);
    try {
      await projectsApi.updateTimeEntry(entry.id, {
        task_id: taskId === NO_TASK ? null : taskId,
        start_at: start,
        end_at: end,
        notes: notes || null,
        billable,
        hourly_rate: hourlyRate.trim() ? Number(hourlyRate) : null,
      });
      toast.success('Entry updated');
      onSaved();
    } catch (err: any) {
      toast.error('Update failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit time entry</DialogTitle>
          <DialogDescription>Adjust the task, times, or billing for this entry.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Task (optional)</Label>
            <Select value={taskId} onValueChange={setTaskId}>
              <SelectTrigger><SelectValue placeholder="(none)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TASK}>(none)</SelectItem>
                {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>Start time</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>End date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>End time</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 items-end">
            <label className="flex items-center gap-2.5 text-[13px] text-foreground cursor-pointer select-none h-10">
              <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-saffron" />
              Billable
            </label>
            <div className="grid gap-1.5">
              <Label>Rate override (optional)</Label>
              <Input type="number" min="0" step="0.01" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} placeholder="Project default" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
